import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Language } from '../services/preferences.types';
import { FALLBACK_LANGUAGE, LANGUAGE_FILE, isSupportedLanguage } from './languages';

interface LangFile {
  LanguageID?: string;
  Name?: string;
  Values: Record<string, string>;
}

/**
 * Runtime translation.
 *
 * DICTIONARIES ARE FETCHED, NOT COMPILED IN. Angular's own i18n builds one bundle
 * per locale and picks between them at deploy time, which cannot express "this
 * user reads German and that one reads Dutch, in the same deployment, decided by
 * a preference that changes at runtime". So the dictionaries are plain assets and
 * a language change is a fetch, not a rebuild and redeploy.
 *
 * THE LOOKUP NEVER RETURNS EMPTY. It falls back requested -> English -> the key
 * itself. A missing translation shows English; a missing KEY shows the key, which
 * is ugly on purpose — it is visible in a screenshot, whereas a blank string
 * silently deletes a label and looks like a layout bug.
 */
@Injectable({ providedIn: 'root' })
export class TranslationService {
  /** Bumped on every successful load. The pipe reads it to invalidate its cache. */
  readonly version$ = new BehaviorSubject<number>(0);
  readonly language$ = new BehaviorSubject<Language>(FALLBACK_LANGUAGE);

  private dictionary: Record<string, string> = {};
  private fallback: Record<string, string> = {};
  private readonly cache = new Map<Language, Record<string, string>>();
  private loading: Language | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Load a language, and English alongside it if not already held.
   *
   * English is loaded EAGERLY as the fallback rather than lazily on the first
   * miss: a miss happens during rendering, and a fetch cannot be awaited there —
   * the label would have to render wrong once and then change under the reader.
   */
  use(code: string | null | undefined): Observable<Language> {
    const language: Language = isSupportedLanguage(code) ? code : FALLBACK_LANGUAGE;
    if (this.language$.value === language && this.version$.value > 0) return of(language);
    if (this.loading === language) return of(language);
    this.loading = language;

    return this.fetch(language).pipe(
      tap((values) => {
        this.dictionary = values;
        if (language === FALLBACK_LANGUAGE) this.fallback = values;
        this.language$.next(language);
        this.version$.next(this.version$.value + 1);
        this.loading = null;
      }),
      map(() => language),
      catchError(() => {
        this.loading = null;
        return of(language);
      })
    );
  }

  /** Ensure English is in hand so the fallback chain has something to fall back to. */
  preloadFallback(): Observable<unknown> {
    if (Object.keys(this.fallback).length) return of(null);
    return this.fetch(FALLBACK_LANGUAGE).pipe(tap((v) => (this.fallback = v)));
  }

  /**
   * PROVENANCE, and a known-stale caveat — accepted as documented, 2026-09-05.
   *
   * The 45 desktop-inherited values in these dictionaries were copied from the
   * newest READABLE copy of the viscom desktop Localisation directory, not from
   * the authoritative branch. That branch was TESTED and is unreachable: the
   * commit object is absent locally and the remote refuses this account
   * (HTTP Basic: Access denied). So this is a measured limitation, not an
   * assumption anyone made.
   *
   * Known concrete risk: three languages' values for the heat-input family differ
   * between the readable copy and the authoritative branch — which is how the
   * staleness was found. NONE of those keys is among the 45 inherited here, so
   * this delivery is not known to be affected. It is also not verified.
   *
   * If the branch becomes reachable, refresh before freezing any shared-tab
   * string. Both steps are deterministic — same inputs, same output:
   *
   *   python3 scripts/build_webui_mapping.py <authoritative-Localisation>
   *   python3 scripts/build_webui_dicts.py --out <delivery-dir>
   *   node tools/i18n-install-dicts.js <delivery-dir>
   */
  private fetch(language: Language): Observable<Record<string, string>> {
    const cached = this.cache.get(language);
    if (cached) return of(cached);
    return this.http.get<LangFile>(`assets/i18n/${LANGUAGE_FILE[language]}.lang`).pipe(
      map((file) => file?.Values ?? {}),
      tap((values) => this.cache.set(language, values)),
      catchError((err) => {
        console.warn(`TranslationService > could not load ${language}`, err);
        return of({});
      })
    );
  }

  /**
   * Translate a key.
   *
   * `params` substitutes {name} placeholders. Interpolation lives here rather
   * than in callers concatenating strings, because a translator must be able to
   * MOVE a value within the sentence — word order is exactly what differs between
   * these six languages, and a concatenated string cannot be reordered at all.
   */
  t(key: string, params?: Record<string, string | number>): string {
    const raw = this.dictionary[key] ?? this.fallback[key] ?? key;
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (whole, name) =>
      Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole
    );
  }

  /** True when the key exists in the active or fallback dictionary. For diagnostics. */
  has(key: string): boolean {
    return key in this.dictionary || key in this.fallback;
  }
}
